import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BtnPrimary, BtnSecondary, BtnDestructive, Pagination } from "@/pages/Senders";
import BatchConnectModal from "@/pages/BatchConnectModal";
import {
  TreeStructure, Plus, Trash, TestTube, GoogleLogo, MagnifyingGlass, CheckCircle, Warning, StackPlus, Broom,
} from "@phosphor-icons/react";

/**
 * Seed Gmail mailboxes — OAuth-connected Gmail inboxes used exclusively as
 * inbox-placement receivers. Click "Connect Gmail" → OAuth popup → we store
 * the encrypted refresh_token and load-balance across the OAuth project pool.
 */
export default function SeedMailboxes() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [cleaningId, setCleaningId] = useState(null);
  const [cleaningAll, setCleaningAll] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const popupRef = useRef(null);

  const load = async () => {
    const r = await api.get("/seed-mailboxes", {
      params: { limit: pageSize, skip: page * pageSize, search: search.trim() || undefined },
    });
    setRows(r.data.mailboxes || []);
    setTotal(r.data.total || 0);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, pageSize]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); load(); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const startConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/oauth/gmail/start", {});
      // Open popup and subscribe to 3 signal channels
      popupRef.current = window.open(data.consent_url,
        `warmly-gmail-oauth-${Date.now()}`,
        "popup=yes,width=520,height=640,noopener=no,noreferrer=no");
      if (!popupRef.current) {
        toast.error("Popup blocked — allow popups for this site");
        setConnecting(false);
        return;
      }

      const cleanup = () => {
        window.removeEventListener("message", onMsg);
        window.removeEventListener("storage", onStorage);
        try { bc.close(); } catch (_e) {}
        clearInterval(pollClose);
        setConnecting(false);
      };
      const finish = (payload) => {
        if (payload?.ok) {
          toast.success(`${payload.email || "Gmail"} ${payload.outcome || "connected"}`);
        } else if (payload) {
          toast.error(payload.message || "Connect failed");
        }
        try { popupRef.current?.close(); } catch (_e) {}
        cleanup();
        load();
      };
      const onMsg = (ev) => {
        const d = ev?.data;
        if (d && d.type === "gmail-oauth-result") finish(d);
      };
      const onStorage = (ev) => {
        if (ev.key === "warmly-oauth-last" && ev.newValue) {
          try {
            const d = JSON.parse(ev.newValue);
            if (d.type === "gmail-oauth-result") finish(d);
          } catch (_e) {}
        }
      };
      window.addEventListener("message", onMsg);
      window.addEventListener("storage", onStorage);
      const bc = new BroadcastChannel("warmly-oauth");
      bc.onmessage = (ev) => {
        if (ev?.data?.type === "gmail-oauth-result") finish(ev.data);
      };
      const pollClose = setInterval(() => {
        if (popupRef.current?.closed) {
          cleanup();
        }
      }, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Gmail OAuth");
      setConnecting(false);
    }
  };

  const testOne = async (id) => {
    setTestingId(id);
    try {
      const { data } = await api.post(`/seed-mailboxes/${id}/test`);
      toast[data.ok ? "success" : "error"](data.message);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTestingId(null); }
  };

  const remove = async (id, email) => {
    if (!confirm(`Disconnect ${email}? You'll need to re-authorize to reconnect.`)) return;
    try {
      await api.delete(`/seed-mailboxes/${id}`);
      toast.success(`${email} disconnected`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const cleanOne = async (id, email) => {
    if (!confirm(`Move ALL messages in Inbox + Spam to Trash for ${email}?\n\nThis empties the mailbox for a fresh placement test.`)) return;
    setCleaningId(id);
    try {
      const { data } = await api.post(`/seed-mailboxes/${id}/purge`);
      toast[data.ok ? "success" : "error"](`${email} — ${data.message || (data.ok ? "cleaned" : "failed")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clean failed");
    } finally {
      setCleaningId(null);
    }
  };

  const cleanAll = async () => {
    if (!confirm(`Move ALL messages in Inbox + Spam to Trash across EVERY connected seed mailbox?\n\nThis wipes inboxes for a fresh placement test and cannot be undone from here.`)) return;
    setCleaningAll(true);
    try {
      const { data } = await api.post(`/seed-mailboxes/purge-all`);
      toast.success(`Cleaned ${data.total} mailbox${data.total === 1 ? "" : "es"} — trashed ${data.trashed} messages`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clean-all failed");
    } finally {
      setCleaningAll(false);
    }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="seed-mailboxes-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-mono uppercase tracking-wider flex items-center gap-2">
            <TreeStructure size={22} weight="bold" className="text-[#E1FF00]" /> Seed Gmail
          </h1>
          <p className="text-xs text-[#71717A] mt-1 font-mono max-w-2xl">
            OAuth-connected Gmail inboxes used exclusively for inbox-placement testing.
            Requires at least one healthy project in <a className="underline text-[#00E5FF]" href="/oauth-projects">OAuth Projects</a>.
          </p>
        </div>
        <div className="flex gap-2">
          <BtnSecondary onClick={cleanAll} disabled={cleaningAll} testid="seed-clean-all-btn">
            <Broom size={13} weight="bold" /> {cleaningAll ? "Cleaning..." : "Clean all"}
          </BtnSecondary>
          <BtnSecondary onClick={() => setBatchOpen(true)} testid="seed-batch-btn">
            <StackPlus size={13} weight="bold" /> Batch connect
          </BtnSecondary>
          <BtnPrimary onClick={startConnect} disabled={connecting} testid="seed-connect-btn">
            <GoogleLogo size={13} weight="bold" /> {connecting ? "Waiting for popup..." : "Connect Gmail"}
          </BtnPrimary>
        </div>
      </div>

      {batchOpen && <BatchConnectModal onClose={() => setBatchOpen(false)} onCompleted={load} />}

      <div className="mb-3 relative max-w-md">
        <MagnifyingGlass size={12} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-[#71717A]" />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="search email, name, or label..."
          data-testid="seed-search"
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] pl-7 pr-3 py-2 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
      </div>

      <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
        <table className="dtable w-full" data-testid="seed-mailboxes-table">
          <thead>
            <tr>
              <th>Email</th><th>Name</th><th>Project</th><th>Health</th><th>Connected</th>
              <th style={{ width: 240 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center text-[#71717A] py-8 font-mono">
                [ no seed mailboxes yet — click "Connect Gmail" ]
              </td></tr>
            )}
            {rows.map((m) => (
              <tr key={m.id}>
                <td className="font-mono text-xs">{m.email}</td>
                <td className="font-mono text-[11px] text-[#71717A]">{m.name || "—"}</td>
                <td className="font-mono text-[10px] text-[#71717A]">{m.oauth_project_name || "—"}</td>
                <td>
                  {m.last_test_ok === true && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono text-[#00E5FF]" title={m.last_test_message}>
                      <CheckCircle size={11} weight="fill" /> ok
                    </span>
                  )}
                  {m.last_test_ok === false && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono text-[#FF3B30]" title={m.last_test_message}>
                      <Warning size={11} weight="fill" /> broken
                    </span>
                  )}
                  {m.last_test_ok == null && <span className="text-[11px] text-[#71717A] font-mono">untested</span>}
                </td>
                <td className="font-mono text-[10px] text-[#71717A]">{(m.created_at || "").slice(0, 10)}</td>
                <td>
                  <div className="flex gap-1.5">
                    <BtnSecondary onClick={() => testOne(m.id)} testid={`seed-test-${m.id}`}>
                      <TestTube size={11} weight="bold" /> {testingId === m.id ? "..." : "Test"}
                    </BtnSecondary>
                    <BtnSecondary onClick={() => cleanOne(m.id, m.email)} testid={`seed-clean-${m.id}`}>
                      <Broom size={11} weight="bold" /> {cleaningId === m.id ? "..." : "Clean"}
                    </BtnSecondary>
                    <BtnDestructive onClick={() => remove(m.id, m.email)} testid={`seed-delete-${m.id}`}>
                      <Trash size={11} weight="bold" />
                    </BtnDestructive>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
        sizeOptions={[25, 50, 200]}
        testidPrefix="seed-pg"
      />
    </div>
  );
}
